/**
 * Launch splash (port of TeaCheck/App/SplashScreenView.swift), full-bleed in its parent.
 *
 * It never restarts: the inline script of index.html (built from boot.ts) already painted and started
 * the splash before the app bundle loaded; this component claims that live element
 * (`window.__dtSplash`) and moves it into its own box. Without a boot splash (e.g. a later mount), it
 * lazily loads the engine and starts one on the same clock (`window.__dtBootT0` for the first one).
 *
 * `exiting` asks for the exit; the splash picks the moment itself (the next calm point of its loop,
 * never before 3.4 s; see `calmExitTime`), plays it (320 ms dissolve with a push into the logo; a
 * 200 ms fade under Reduce Motion), then calls `onExited` once. Unmounted while that exit is pending
 * or playing, the splash finishes it on its own above the page instead of being cut.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { SplashHandle } from './engine';
import { splashStrings } from './bootLocale';

export interface SplashSceneProps {
  exiting?: boolean;
  onExited?: () => void;
}

/** A splash released by an unmount survives one task, so a StrictMode remount claims it back. */
let pendingRelease: ReturnType<typeof setTimeout> | undefined;
let bootSplashUsed = false;

function claimLiveSplash(): SplashHandle | undefined {
  const live = window.__dtSplash;
  if (!live || live.destroyed) return undefined;
  if (pendingRelease !== undefined) { clearTimeout(pendingRelease); pendingRelease = undefined; }
  live.claimed = true;
  bootSplashUsed = true;
  return live;
}

export function SplashScene({ exiting = false, onExited }: SplashSceneProps) {
  const host = useRef<HTMLDivElement>(null);
  const handle = useRef<SplashHandle | undefined>(undefined);
  const [attached, setAttached] = useState(false);
  const onExitedRef = useRef(onExited);
  useEffect(() => { onExitedRef.current = onExited; }, [onExited]);
  const exited = useRef(false);
  const exitingRef = useRef(exiting);
  useEffect(() => { exitingRef.current = exiting; }, [exiting]);

  // Layout effect: the boot element moves in before the browser paints this commit (no blank frame).
  useLayoutEffect(() => {
    const box = host.current;
    if (!box) return;
    let cancelled = false;
    const attach = (h: SplashHandle) => {
      h.el.style.position = 'absolute';
      h.el.style.zIndex = '';
      if (h.el.parentNode !== box) box.appendChild(h.el);
      handle.current = h;
      setAttached(true);
    };
    const live = claimLiveSplash();
    if (live) attach(live);
    else if (!exitingRef.current) {
      void import('./engine').then(({ createSplash }) => {
        if (cancelled || exitingRef.current) return;
        const raced = claimLiveSplash();
        if (raced) { attach(raced); return; }
        const [label, ariaLabel] = splashStrings();
        const t0 = !bootSplashUsed && typeof window.__dtBootT0 === 'number' ? window.__dtBootT0 : performance.now();
        bootSplashUsed = true;
        const created = createSplash({
          t0,
          label,
          ariaLabel,
          reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
        });
        created.claimed = true;
        window.__dtSplash = created;
        attach(created);
      });
    }
    return () => {
      cancelled = true;
      const h = handle.current;
      handle.current = undefined;
      if (!h) return;
      if (h.exitRequested && !h.destroyed) {
        // Unmounted mid-exit (e.g. a safety timeout of the owner): finish the dissolve above the page;
        // the exit effect below destroys it when done.
        h.el.style.position = 'fixed';
        h.el.style.zIndex = '1000';
        document.body.appendChild(h.el);
        return;
      }
      h.claimed = false;
      if (pendingRelease !== undefined) clearTimeout(pendingRelease);
      pendingRelease = setTimeout(() => {
        pendingRelease = undefined;
        if (!h.claimed) {
          h.destroy();
          if (window.__dtSplash === h) window.__dtSplash = undefined;
        }
      }, 0);
    };
  }, []);

  useEffect(() => {
    if (!exiting) return;
    const finish = () => {
      if (exited.current) return;
      exited.current = true;
      onExitedRef.current?.();
    };
    const h = handle.current;
    if (h) {
      void h.exit().then(() => {
        h.destroy();
        if (window.__dtSplash === h) window.__dtSplash = undefined;
        finish();
      });
    } else {
      // No engine yet (lazy fallback still loading): fade the night box itself.
      const box = host.current;
      const anim = box?.animate?.([{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: 'ease-out', fill: 'forwards' });
      if (anim) anim.addEventListener('finish', finish);
      setTimeout(finish, 450);
    }
  }, [exiting, attached]);

  return (
    <div
      ref={host}
      data-dt-splash=""
      className="absolute inset-0 overflow-hidden"
      // Night until the engine element is inside; then transparent so its own fade reveals the screen.
      style={{ background: attached ? 'transparent' : '#07060b' }}
    />
  );
}
